
// Converts arbitrary vector data (GeoJSON, and the GeoJSON a Shapefile is
// read into by the frontend, and a parsed DXF) into the same {owners,
// features} "part" shape LERParser.js's extractGenericWFSPart already
// produces for a customer WFS layer - so it can go through the exact same
// Potree.finalizeLERParts()/LERLayer pipeline as LER data, WFS data and
// everything else in gis.vue, with no separate rendering path.
//
// This is deliberately NOT LER-specific: there is no schema to detect, no
// supply/owner attribute convention to rely on - "supply" (only used for
// coloring) is guessed from whatever type/layer-like text is available
// (guessSupplyFromLayerName, the same heuristic already used for a
// customer WFS layer's own name), and every feature's Z is either its own
// real value or draped onto the ground (never offset below it like a
// buried utility line - there's no reason to assume that for arbitrary
// vector data).
//
// Both formats can contain element/geometry types this pipeline has no
// rendering for (LER.js only knows "line" and "point" - see its
// OwnerGroup.addFeature). Per the brief this was built to (a large real
// DXF/GeoJSON file with a handful of odd elements should still load,
// not abort) - anything unsupported is skipped rather than raising an
// error, and counted (by type) in the returned `skipped` map so gis.vue
// can tell the user what was left out.

import {GENERIC_ATTRIBUTE_PATTERNS, DEFAULT_DIAMETER_M, toMetersGuessingUnit, guessSupplyFromLayerName} from "./LERParser.js";

import {t} from "../../digitization/i18n/index.js";
function findAttributeMatchingInObject(properties, patterns){
	if(!properties){
		return null;
	}
	for(let key of Object.keys(properties)){
		let value = properties[key];
		if(value === null || value === undefined || value === ""){
			continue;
		}
		let name = key.toLowerCase();
		if(patterns.some(p => name.includes(p))){
			return {name: key, value: String(value)};
		}
	}
	return null;
}

// Real (nonzero, finite) Z is kept; anything else - missing entirely (a
// plain [x, y] pair) or exactly 0, the common "no elevation given" default
// in both GeoJSON and DXF - is draped onto the ground instead.
function resolveOnGround(coords, groundAt){
	let resolved = [];
	let sources = [];
	for(let [x, y, z] of coords){
		if(Number.isFinite(z) && z !== 0){
			resolved.push([x, y, z]);
			sources.push("measured");
		}else{
			resolved.push([x, y, groundAt(x, y)]);
			sources.push("ground_level");
		}
	}
	return {resolved, sources};
}

// Kun simple værdier (tekst/tal) tages med: de bruges til at farve et lag efter en attribut (se LayerStyle.js)
function scalarAttributes(properties){
	let attrs = {};
	let count = 0;
	for(let key of Object.keys(properties || {})){
		let value = properties[key];
		if(value instanceof Date && !Number.isNaN(value.getTime())) value = value.toISOString().slice(0, 10); // dbf-datoer
		if(value === null || value === undefined || typeof value === "object" || value === "") continue;
		attrs[key] = value;
		if(++count >= 80) break;
	}
	return attrs;
}

function buildGenericFeature(coords, properties, groundAt, layerLabel, geometryKind){
	let diameterAttr = findAttributeMatchingInObject(properties, GENERIC_ATTRIBUTE_PATTERNS.diameter);
	let diameterM = diameterAttr ? toMetersGuessingUnit(parseFloat(diameterAttr.value)) : null;
	if(diameterM !== null && Number.isNaN(diameterM)) diameterM = null;

	let ownerAttr = findAttributeMatchingInObject(properties, GENERIC_ATTRIBUTE_PATTERNS.owner);
	let idAttr = findAttributeMatchingInObject(properties, GENERIC_ATTRIBUTE_PATTERNS.id);
	let statusAttr = findAttributeMatchingInObject(properties, GENERIC_ATTRIBUTE_PATTERNS.status);
	let dangerAttr = findAttributeMatchingInObject(properties, GENERIC_ATTRIBUTE_PATTERNS.danger);
	let typeAttr = findAttributeMatchingInObject(properties, GENERIC_ATTRIBUTE_PATTERNS.type);

	let {resolved, sources} = resolveOnGround(coords, groundAt);
	let ownerLabel = ownerAttr ? ownerAttr.value : layerLabel;

	return {
		owner: ownerLabel, ownerName: ownerLabel, id: idAttr ? idAttr.value : null,
		type: typeAttr ? typeAttr.value : layerLabel, supply: guessSupplyFromLayerName(typeAttr ? typeAttr.value : layerLabel),
		geometry: geometryKind, is_trace: false,
		diameter_m: diameterM || DEFAULT_DIAMETER_M,
		subtype: typeAttr ? typeAttr.value : null,
		status: statusAttr ? statusAttr.value : null,
		danger: dangerAttr ? dangerAttr.value : null,
		attrs: scalarAttributes(properties),
		z_sources: sources, coords: resolved,
	};
}

function coord3(c){
	return [c[0], c[1], c.length > 2 && Number.isFinite(c[2]) ? c[2] : 0];
}

// A ring's own first/last point are already identical in a well-formed
// GeoJSON polygon, but this is cheap insurance against a source that omits
// the closing vertex.
function closeRing(coords){
	if(coords.length < 2){
		return coords;
	}
	let [fx, fy, fz] = coords[0];
	let [lx, ly, lz] = coords[coords.length - 1];
	return (fx === lx && fy === ly && fz === lz) ? coords : [...coords, coords[0]];
}

// Yields {coords, geometryKind} for every part of a GeoJSON geometry this
// pipeline can show - a polygon (no fill rendering exists here) becomes one
// closed line per ring (outer + holes), same stand-in as an area digitized
// with the "polygon" measuring tool. Anything not listed (a future GeoJSON
// geometry type) is simply not yielded - the caller counts that as skipped.
function* partsFromGeoJSONGeometry(geometry){
	if(!geometry){
		return;
	}

	switch(geometry.type){
		case "Point":
			yield {coords: [coord3(geometry.coordinates)], geometryKind: "point"};
			break;
		case "MultiPoint":
			for(let c of geometry.coordinates) yield {coords: [coord3(c)], geometryKind: "point"};
			break;
		case "LineString":
			yield {coords: geometry.coordinates.map(coord3), geometryKind: "line"};
			break;
		case "MultiLineString":
			for(let line of geometry.coordinates) yield {coords: line.map(coord3), geometryKind: "line"};
			break;
		case "Polygon":
			for(let ring of geometry.coordinates) yield {coords: closeRing(ring.map(coord3)), geometryKind: "line"};
			break;
		case "MultiPolygon":
			for(let polygon of geometry.coordinates){
				for(let ring of polygon) yield {coords: closeRing(ring.map(coord3)), geometryKind: "line"};
			}
			break;
		case "GeometryCollection":
			for(let sub of geometry.geometries || []) yield* partsFromGeoJSONGeometry(sub);
			break;
		// default: unsupported geometry type (or none of the above) - skipped.
	}
}

// geojson: a FeatureCollection (a bare Feature or Geometry is also
// accepted, wrapped as a single-feature collection) - already in the same
// coordinate system as the point cloud (gis.vue reprojects a plainly
// lon/lat GeoJSON before calling this; a Shapefile's own features, which
// carry no CRS info at all, are assumed to already match, exactly like a
// GML import always has been).
// layerLabel: the file name, used as both the fallback "owner" bucket
// (every feature groups under one entry in the GIS tree, like a WFS
// layer does) and the fallback type/supply guess when a feature has none
// of its own.
// A big enough file (thousands of features/entities) parsed in one
// synchronous burst can block the tab's main thread long enough for the
// browser to kill it as unresponsive - same failure mode as, and same fix
// as, LERParser.js's own extractFeatures (see its comment for the actual
// incident this was found from). Yielding periodically here too costs
// nothing for a normal-sized file and avoids that entirely for a large one.
const YIELD_EVERY_N = 200;
const yieldToBrowser = () => new Promise(resolve => setTimeout(resolve, 0));

export async function featuresFromGeoJSON(geojson, groundAt, layerLabel){
	let owners = {};
	let features = [];
	let skipped = {};

	let inputFeatures = [];
	if(geojson && geojson.type === "FeatureCollection"){
		inputFeatures = geojson.features || [];
	}else if(geojson && geojson.type === "Feature"){
		inputFeatures = [geojson];
	}else if(geojson && geojson.type){
		inputFeatures = [{type: "Feature", properties: {}, geometry: geojson}];
	}

	let i = 0;
	for(let f of inputFeatures){
		let properties = f.properties || {};
		let any = false;
		for(let {coords, geometryKind} of partsFromGeoJSONGeometry(f.geometry)){
			if(coords.length === 0 || (geometryKind === "line" && coords.length < 2)){
				continue;
			}
			any = true;
			let feature = buildGenericFeature(coords, properties, groundAt, layerLabel, geometryKind);
			if(feature.ownerName && !owners[feature.owner]){
				owners[feature.owner] = feature.ownerName;
			}
			delete feature.ownerName;
			features.push(feature);
		}
		if(!any){
			let key = (f.geometry && f.geometry.type) || t("ukendt geometri");
			skipped[key] = (skipped[key] || 0) + 1;
		}

		i++;
		if(i % YIELD_EVERY_N === 0){
			await yieldToBrowser();
		}
	}

	return {owners, features, skipped};
}

// Entity types with a straightforward line/point equivalent. Everything
// else a real-world DXF carries - TEXT/MTEXT, DIMENSION, HATCH, SOLID,
// 3DFACE, INSERT (block references), SPLINE, ELLIPSE, ... - has either no
// sensible line/point equivalent here or would need real curve/block
// evaluation to place correctly, so it's left out rather than approximated
// badly; see the module comment for why that's a skip, not an error.
const DXF_SUPPORTED_TYPES = new Set(["POINT", "LINE", "LWPOLYLINE", "POLYLINE", "CIRCLE", "ARC"]);

function dxfVertex(v){
	return [v.x, v.y, Number.isFinite(v.z) ? v.z : 0];
}

// entity.startAngle/endAngle (CIRCLE/ARC) are already radians - dxf-parser
// itself converts from the file's degrees at parse time.
function sampleArc(center, radius, startAngle, endAngle, segments){
	let end = endAngle;
	if(end <= startAngle){
		end += Math.PI * 2;
	}
	let coords = [];
	let z = Number.isFinite(center.z) ? center.z : 0;
	for(let i = 0; i <= segments; i++){
		let a = startAngle + (end - startAngle) * (i / segments);
		coords.push([center.x + Math.cos(a) * radius, center.y + Math.sin(a) * radius, z]);
	}
	return coords;
}

// Yields {coords, geometryKind} for one DXF entity, or nothing for an
// unsupported/malformed one (see DXF_SUPPORTED_TYPES).
function* partsFromDxfEntity(entity){
	switch(entity.type){
		case "POINT":
			if(entity.position) yield {coords: [dxfVertex(entity.position)], geometryKind: "point"};
			break;
		case "LINE":
			if(entity.vertices && entity.vertices.length >= 2){
				yield {coords: entity.vertices.map(dxfVertex), geometryKind: "line"};
			}
			break;
		case "LWPOLYLINE":
		case "POLYLINE": {
			// POLYLINE also covers old-style 3D polygon/polyface meshes - not a
			// line chain at all, so left as unsupported rather than drawing
			// nonsense edges through it.
			if(entity.isPolyfaceMesh || entity.is3dPolygonMesh){
				break;
			}
			let coords = (entity.vertices || []).map(dxfVertex);
			// Curved (bulge) segments are drawn as a straight chord instead of
			// the true arc - a deliberate, documented simplification (see
			// module comment), not a skip: the polyline as a whole still loads.
			if(entity.shape && coords.length >= 2){
				coords = closeRing(coords);
			}
			if(coords.length >= 2){
				yield {coords, geometryKind: "line"};
			}
			break;
		}
		case "CIRCLE":
			if(entity.center && Number.isFinite(entity.radius)){
				yield {coords: sampleArc(entity.center, entity.radius, 0, Math.PI * 2, 48), geometryKind: "line"};
			}
			break;
		case "ARC":
			if(entity.center && Number.isFinite(entity.radius)){
				yield {coords: sampleArc(entity.center, entity.radius, entity.startAngle, entity.endAngle, 24), geometryKind: "line"};
			}
			break;
		// default: not in DXF_SUPPORTED_TYPES - handled by the caller.
	}
}

// parsedDxf: the object returned by `new DxfParser().parseSync(text)`
// (dxf-parser). Coordinates are assumed to already be in the point cloud's
// own local/projected system - a DXF carries no CRS of its own, and a
// survey/CAD export is essentially never in geographic lon/lat, unlike a
// GeoJSON export (see gis.vue's own lon/lat check, only applied there).
//
// Each entity's own DXF layer becomes its owner bucket (rather than one
// bucket for the whole file, as featuresFromGeoJSON does) - a DXF's layers
// are already the natural, author-intended grouping (cf. a WFS's own
// per-layer buckets), and is also where guessSupplyFromLayerName has an
// actual name to work from.
export async function featuresFromDxf(parsedDxf, groundAt, fileLabel){
	let owners = {};
	let features = [];
	let skipped = {};

	let i = 0;
	for(let entity of (parsedDxf && parsedDxf.entities) || []){
		if(!DXF_SUPPORTED_TYPES.has(entity.type)){
			skipped[entity.type] = (skipped[entity.type] || 0) + 1;
			continue;
		}

		let layerName = entity.layer || fileLabel;
		let any = false;

		for(let {coords, geometryKind} of partsFromDxfEntity(entity)){
			any = true;
			let {resolved, sources} = resolveOnGround(coords, groundAt);
			features.push({
				owner: layerName, id: entity.handle || null,
				type: layerName, supply: guessSupplyFromLayerName(layerName),
				geometry: geometryKind, is_trace: false,
				diameter_m: DEFAULT_DIAMETER_M,
				subtype: null, status: null, danger: null,
				z_sources: sources, coords: resolved,
			});
			if(!owners[layerName]){
				owners[layerName] = layerName;
			}
		}

		if(!any){
			// A supported type whose geometry was nonetheless unusable (e.g. a
			// POLYLINE with under 2 vertices, or a mesh flavor of POLYLINE) -
			// counted the same way as a genuinely unsupported type.
			skipped[entity.type] = (skipped[entity.type] || 0) + 1;
		}

		i++;
		if(i % YIELD_EVERY_N === 0){
			await yieldToBrowser();
		}
	}

	return {owners, features, skipped};
}
